/*
  Objects Array = [
    {
      ballon: {
        clusterCaption: "List of objects at the coordinates",
        balloonContentHeader,
        balloonContentBody,
        balloonContentFooter
      }
    }
  ]
  EVENTS 
  onClusterClick - returns array of objects at that cluster
  handleClick - return clicked object
*/

import React from "react";
import { YMaps, Map, ZoomControl, FullscreenControl } from "react-yandex-maps";
import Loading from "../common/LoadingCss";

class YandexHeatMapWrap extends React.Component {
  _isMounted = false;
  heatmapScriptLoaded = false;

  constructor() {
    super();
    this.state = {
      loading: true,
      center: [49.807754, 73.088504],
      objects: [],
    };
    this.clusterer = null;
    this.ymaps = null;
    this.map = null;
    this.heatmap = null;
  }

  onApiAvaliable = (ymaps) => {
    this.ymaps = ymaps;
    if (this.map && !this.heatmap) {
      this.initHeatMap();
    }
  };

  /**
   * Локальная загрузка скрипта тепловой карты из public:
   * public/js/heatmap.min.js
   */
  loadHeatmapScript = () => {
    if (this.heatmapScriptLoaded) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-yandex-heatmap="true"]',
      );

      if (existing) {
        this.heatmapScriptLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement("script");
      script.src = `${process.env.PUBLIC_URL}/js/heatmap.min.js`;
      script.async = true;
      script.dataset.yandexHeatmap = "true";

      script.onload = () => {
        this.heatmapScriptLoaded = true;
        resolve();
      };

      script.onerror = (err) => {
        console.error("Не удалось загрузить скрипт yandex-heatmap", err);
        reject(err);
      };

      document.body.appendChild(script);
    });
  };

  setMapControlInstanceRef = (ref) => {
    this.map = ref;
    if (!this.map) return;

    if (!this.heatmap) {
      if (this.ymaps) {
        this.initHeatMap();
      }
    } else {
      this.heatmap.setMap(this.map);
      this.setState({ loading: false });
    }
  };

  calculateDateRange = () => {
    const {
      dateRange: [start, end],
    } = this.props;
    return Math.abs(start.diff(end, "days")) + 1;
  };

  getMidPointIntensity = () => {
    const days = this.calculateDateRange();
    if (days >= 0 && days <= 2) return 0.07;

    const intensityRanges = {
      2: 0.07,
      6: 0.009,
      14: 0.006,
      30: 0.005,
      90: 0.004,
      180: 0.003,
    };

    const maxDay = Object.keys(intensityRanges).find((range) => days <= range);
    return intensityRanges[maxDay] || 0.002;
  };

  initHeatMap = () => {
    const ymaps = this.ymaps;
    const map = this.map;

    if (!ymaps || !map) return;

    // Защита от двойного вызова
    if (this.heatmap) return;

    this.loadHeatmapScript()
      .then(() => {
        if (!this._isMounted || this.map !== map) return;

        const { objects } = this.props;

        ymaps.modules.require(["Heatmap"], (Heatmap) => {
          if (!this._isMounted || this.map !== map) return;

          const data1 = {
            type: "FeatureCollection",
            features: this.returnMapData(objects),
          };

          const heatmap = new Heatmap(data1, {
            radius: 20,
            intensityOfMidpoint: this.getMidPointIntensity(),
            gradient: {
              0.1: "rgba(128, 255, 0, 0.7)",
              0.2: "rgba(255, 255, 0, 0.8)",
              0.7: "rgba(234, 72, 58, 0.9)",
              1.0: "rgba(162, 36, 25, 1)",
            },
          });

          heatmap.setMap(map);
          this.heatmap = heatmap;

          if (this._isMounted) {
            this.setState({ loading: false });
          }
        });
      })
      .catch(() => {
        if (this._isMounted) {
          this.setState({ loading: false });
        }
      });
  };

  returnFr(val, max) {
    return (100 * val) / max;
  }

  returnMapData(data) {
    const { xDataKey, yDataKey, sumKey } = this.props;
    const unique_coords = [];
    const arr = data
      .map((item, index) => {
        const coords = item[yDataKey] + " " + item[xDataKey];
        if (!unique_coords.includes(coords)) {
          unique_coords.push(coords);
          return {
            id: "id" + index,
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [item[yDataKey], item[xDataKey]],
            },
            properties: {
              weight: sumKey ? parseFloat(item[sumKey]) : 1,
            },
          };
        }
        return null;
      })
      .filter((e) => e);
    return arr;
  }

  onObjectClick = (obj) => {
    const { idDataKey } = this.props;
    this.props.handleClick(obj[idDataKey]);
    this.changeMapCenter(obj);
  };

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;

    if (this.heatmap) {
      try {
        this.heatmap.setMap(null);
      } catch (e) {}
      this.heatmap = null;
    }
    if (this.map) {
      this.map.destroy();
      this.map = null;
    }
  }

  createGeoObjects = (objects) => {
    const { xDataKey, yDataKey } = this.props;
    const geoObjects = [];
    if (!objects[0]) return [];
    objects.forEach((el) => {
      const coords = [el[yDataKey], el[xDataKey]];
      if (coords) {
        const obj = new this.ymaps.Placemark(coords, el.ballon, {
          iconColor: el.color ? el.color : "#3caa3c",
          preset: "islands#circleIcon",
        });
        obj.dataObject = el;
        obj.events.add("click", () => {
          this.onObjectClick(el);
        });
        geoObjects.push(obj);
      }
    });
    return geoObjects;
  };

  onClustererClick = (ev) => {
    if (!ev.get("target").dataObject) {
      const objects = ev
        .get("target")
        .getGeoObjects()
        .map((el) => el.dataObject);
      if (this.props.onClusterClick) {
        this.props.onClusterClick(objects);
      }
    }
  };

  createCluster = () => {
    const clusterer = new this.ymaps.Clusterer({
      hasBalloon: false,
      clusterDisableClickZoom: true,
      clusterIconLayout: "default#pieChart",
      clusterIconPieChartRadius: 25,
      clusterIconPieChartCoreRadius: 10,
      clusterIconPieChartStrokeWidth: 3,
      groupByCoordinates: false,
    });
    clusterer.events.add("click", (e) => {
      this.onClustererClick(e);
    });
    const geoObjects = this.createGeoObjects(this.state.objects);
    if (geoObjects[0]) clusterer.add(geoObjects);
    this.clusterer = clusterer;
    if (this.map) this.map.geoObjects.add(clusterer);
  };

  componentDidUpdate() {
    if (
      this.state.objects.length !== this.props.objects.length &&
      this._isMounted
    ) {
      this.setState(
        {
          objects: this.props.objects,
        },
        () => {
          if (this.clusterer) this.refreshClusterer();

          if (!this.props.showDefaultCity && this.props.objects[0]) {
            this.changeMapCenter(this.props.objects[0]);
          }

          if (this.heatmap) {
            const data = {
              type: "FeatureCollection",
              features: this.returnMapData(this.props.objects),
            };

            this.heatmap.setData(data);
            if (this.map && this.heatmap) {
              this.heatmap.setMap(this.map);
            }
          }
        },
      );
    }
  }

  changeMapCenter = (el) => {
    const { xDataKey, yDataKey } = this.props;
    const coords = [el[yDataKey], el[xDataKey]];
    this.setState({ center: coords });
  };

  refreshClusterer = () => {
    this.clusterer.removeAll();
    const geoObjects = this.createGeoObjects(this.state.objects);
    if (geoObjects[0]) {
      this.clusterer.add(geoObjects);
    }
  };

  render() {
    const { height, zoom = 13 } = this.props;
    return (
      <React.Fragment>
        {this.state.loading && <Loading />}
        {/* onApiAvaliable — намеренная опечатка, это v2 API react-yandex-maps */}
        <YMaps onApiAvaliable={this.onApiAvaliable}>
          <Map
            state={{
              center: this.state.center,
              zoom: zoom,
              controls: [],
            }}
            width="100%"
            height={height ? height : "55vh"}
            instanceRef={this.setMapControlInstanceRef}
          >
            <ZoomControl
              options={{
                size: "small",
                zoomDuration: 1000,
              }}
            />
            <FullscreenControl />
          </Map>
        </YMaps>
      </React.Fragment>
    );
  }
}

export default YandexHeatMapWrap;
